import React, { useMemo, useState } from "react";
import { useAuth } from "../../common/AuthContext";
import useBrokerOrderWebsocket from "../../../service/websocket/BrokerOrderWebsocket";
import useBrokerPositionWebsocket from "../../../service/websocket/BrokerPositionWebsocket";
import BrokerOrderTable, {
  getOrderStatusCategory,
  ORDER_CATEGORIES,
} from "./BrokerOrderTable";
import "../AlgoTrading.css";
import "./BrokerOrders.css";

const numberValue = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "--";
  return numberValue(value).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatMoney = (value) =>
  `₹${numberValue(value).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatExpiry = (value) => {
  if (!value) return "";
  const match = String(value).toUpperCase().match(/^(\d{2})([A-Z]{3})(\d{4})$/);
  if (!match) return value;
  const [, day, month, year] = match;
  return `${day}-${month[0]}${month.slice(1).toLowerCase()}-${year.slice(-2)}`;
};

const displaySymbol = (position) => {
  const optionType = String(position.optionType || "").toUpperCase();
  if (position.symbolName && position.expiryDate && position.strikePrice
      && optionType && optionType !== "XX") {
    return `${position.symbolName} ${formatExpiry(position.expiryDate)} ${formatNumber(position.strikePrice)} ${optionType}`;
  }
  return position.symbol || "--";
};

const displaySide = (position) => {
  if (position.positionSide === "LONG") return "BUY";
  if (position.positionSide === "SHORT") return "SELL";
  return position.positionSide || "--";
};

const displayProduct = (productType) => {
  const normalized = String(productType || "").toUpperCase();
  if (normalized === "CARRYFORWARD") return "CF";
  if (normalized === "INTRADAY") return "MIS";
  if (normalized === "DELIVERY") return "CNC";
  return productType || "--";
};

const lotDetails = (position) => {
  const lotSize = numberValue(position.lotSize);
  if (lotSize <= 0) return null;
  const lots = Math.abs(numberValue(position.netQuantity)) / lotSize;
  return {
    lots: `${formatNumber(lots)} ${lots === 1 ? "Lot" : "Lots"}`,
    size: `1 Lot = ${formatNumber(lotSize)}`,
  };
};

const positionProfit = (position) => {
  if (position.totalProfit !== null && position.totalProfit !== undefined) {
    return numberValue(position.totalProfit);
  }
  return numberValue(position.realizedProfit) + numberValue(position.unrealizedProfit);
};

const Positions = () => {
  const { user } = useAuth();
  const {
    snapshot: positionSnapshot,
    positions,
    isConnected: isPositionConnected,
    connectionError: positionConnectionError,
  } =
    useBrokerPositionWebsocket(user?.accessToken);
  const {
    snapshot: orderSnapshot,
    orders,
    isConnected: isOrderConnected,
    connectionError: orderConnectionError,
  } = useBrokerOrderWebsocket(user?.accessToken);
  const [activeTab, setActiveTab] = useState("OPEN");
  const [selectedBroker, setSelectedBroker] = useState("");
  const isOrderTab = ORDER_CATEGORIES.includes(activeTab);
  const isConnected = isOrderTab ? isOrderConnected : isPositionConnected;
  const connectionError = isOrderTab
    ? orderConnectionError
    : positionConnectionError;

  const openPositions = useMemo(
    () => positions.filter((position) => position.positionStatus === "OPEN"),
    [positions]
  );
  const displayedPositions = useMemo(
    () => positions.filter((position) =>
      position.positionStatus === activeTab
      && (!selectedBroker || position.brokerAccountId === selectedBroker)
    ),
    [activeTab, positions, selectedBroker]
  );
  const ordersForSelectedBroker = useMemo(
    () => orders.filter(
      (order) => !selectedBroker || order.brokerAccountId === selectedBroker
    ),
    [orders, selectedBroker]
  );
  const orderCategoryCounts = useMemo(
    () => ordersForSelectedBroker.reduce((counts, order) => {
      const category = getOrderStatusCategory(order);
      return { ...counts, [category]: counts[category] + 1 };
    }, { PENDING: 0, EXECUTED: 0, REJECTED: 0, CANCELLED: 0 }),
    [ordersForSelectedBroker]
  );
  const displayedOrders = useMemo(
    () => ordersForSelectedBroker.filter(
      (order) => getOrderStatusCategory(order) === activeTab
    ),
    [activeTab, ordersForSelectedBroker]
  );
  const brokers = useMemo(() => {
    const brokerMap = new Map();
    [...positionSnapshot.brokers, ...orderSnapshot.brokers].forEach((broker) => {
      brokerMap.set(broker.brokerAccountId, broker);
    });
    return [...brokerMap.values()];
  }, [orderSnapshot.brokers, positionSnapshot.brokers]);
  const investedValue = openPositions.reduce(
    (total, position) => total
      + Math.abs(numberValue(position.netQuantity) * numberValue(position.averagePrice)),
    0
  );
  const liveProfit = openPositions.reduce(
    (total, position) => total + positionProfit(position),
    0
  );
  const activeSnapshot = isOrderTab ? orderSnapshot : positionSnapshot;
  const brokerErrors = activeSnapshot.brokers.filter((broker) => !broker.success);

  return (
    <main className="algo-page">
      <header className="algo-page-heading">
        <div><h1>Positions</h1><p>Live positions and today&apos;s broker orders.</p></div>
        <div className={`algo-live-status ${isConnected ? "connected" : ""}`}>
          <span />{isConnected ? "Live · refreshes every 5 seconds" : "Connecting…"}
        </div>
      </header>

      {connectionError && <div className="algo-error algo-order-error">{connectionError}</div>}
      {brokerErrors.map((broker) => (
        <div className="algo-error algo-order-error" key={broker.brokerAccountId}>
          <strong>{broker.brokerAccountName || broker.brokerType}:</strong>{" "}
          {broker.message || (isOrderTab
            ? "Orders are temporarily unavailable."
            : "Positions are temporarily unavailable.")}
        </div>
      ))}

      <section className="algo-kpi-grid">
        <article><span>Open Positions</span><strong>{positionSnapshot.openPositionCount}</strong></article>
        <article><span>Invested Value</span><strong>{formatMoney(investedValue)}</strong></article>
        <article><span>Live P&amp;L</span><strong className={liveProfit >= 0 ? "algo-positive" : "algo-negative"}>{formatMoney(liveProfit)}</strong></article>
      </section>

      <section className="algo-filter-bar">
        <select aria-label="Broker" value={selectedBroker} onChange={(event) => setSelectedBroker(event.target.value)}>
          <option value="">All Brokers</option>
          {brokers.map((broker) => (
            <option key={broker.brokerAccountId} value={broker.brokerAccountId}>
              {broker.brokerAccountName || broker.brokerType}
            </option>
          ))}
        </select>
      </section>

      <section className="algo-content-card">
        <div className="algo-tabs algo-order-tabs" role="tablist" aria-label="Positions and orders">
          <button className={activeTab === "OPEN" ? "active" : ""} onClick={() => setActiveTab("OPEN")}>
            Open Positions <span>{positionSnapshot.openPositionCount}</span>
          </button>
          <button className={activeTab === "CLOSED" ? "active" : ""} onClick={() => setActiveTab("CLOSED")}>
            Closed Positions <span>{positionSnapshot.closedPositionCount}</span>
          </button>
          {ORDER_CATEGORIES.map((category) => (
            <button
              className={activeTab === category ? "active" : ""}
              key={category}
              onClick={() => setActiveTab(category)}
              role="tab"
              aria-selected={activeTab === category}
            >
              {category[0]}{category.slice(1).toLowerCase()}
              <span>{orderCategoryCounts[category]}</span>
            </button>
          ))}
        </div>
        <div className="algo-order-summary">
          <span>{activeSnapshot.connectedBrokerCount} connected broker(s)</span>
          {isOrderTab && <span>{displayedOrders.length} {activeTab.toLowerCase()} / {orderSnapshot.totalOrders} total</span>}
          {activeSnapshot.refreshedAt && <span>Updated {new Date(activeSnapshot.refreshedAt).toLocaleTimeString("en-IN")}</span>}
        </div>
        {isOrderTab ? (
          <BrokerOrderTable
            orders={displayedOrders}
            isConnected={isOrderConnected}
            category={activeTab}
          />
        ) : (
          <div className="algo-table-wrap">
          <table className="algo-table">
            <thead><tr><th>Symbol</th><th>Broker</th><th>Side</th><th>Product</th><th>Net Qty</th><th>Average</th><th>LTP</th><th>P&amp;L</th></tr></thead>
            <tbody>
              {displayedPositions.map((position, index) => {
                const profit = positionProfit(position);
                const lots = lotDetails(position);
                const percentChange = position.percentChange === null
                  || position.percentChange === undefined
                  ? null
                  : numberValue(position.percentChange);
                return (
                  <tr key={`${position.brokerAccountId}-${position.positionId || position.symbol || index}`}>
                    <td>{displaySymbol(position)}<small>{position.exchange || ""}</small></td>
                    <td>{position.brokerAccountName || position.brokerType || "--"}<small>{position.brokerType || ""}</small></td>
                    <td className={position.positionSide === "LONG" ? "algo-positive" : position.positionSide === "SHORT" ? "algo-negative" : ""}>{displaySide(position)}</td>
                    <td>{displayProduct(position.productType)}<small>{formatExpiry(position.expiryDate)}</small></td>
                    <td>{lots ? lots.lots : formatNumber(position.netQuantity)}<small>{lots ? lots.size : `${formatNumber(position.buyQuantity)} buy / ${formatNumber(position.sellQuantity)} sell`}</small></td>
                    <td>{position.averagePrice === null || position.averagePrice === undefined ? "--" : formatMoney(position.averagePrice)}</td>
                    <td>{position.lastPrice === null || position.lastPrice === undefined ? "--" : formatMoney(position.lastPrice)}</td>
                    <td className={profit >= 0 ? "algo-positive" : "algo-negative"}>{formatMoney(profit)}<small>{percentChange === null ? position.positionStatus : `${percentChange >= 0 ? "+" : ""}${percentChange.toFixed(2)}%`}</small></td>
                  </tr>
                );
              })}
              {!displayedPositions.length && (
                <tr><td colSpan="8"><div className="algo-empty compact">
                  <i className="fa fa-briefcase" />
                  <h2>No {activeTab.toLowerCase()} positions</h2>
                  <p>{isConnected ? "The next broker refresh will appear automatically." : "Waiting for the secure position connection."}</p>
                </div></td></tr>
              )}
            </tbody>
          </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default Positions;
